import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { KeyRound, LogOut } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';

const detailText = (d) => (typeof d === 'string' ? d : Array.isArray(d) ? d.map(x => x?.msg).filter(Boolean).join(' ') : 'Could not change password');

// Self-service password change; also the forced screen after an admin sets a temporary password.
export default function ChangePasswordPage() {
  const { api, user, setUser, logout } = useAuth();
  const nav = useNavigate();
  const forced = !!user?.must_change_password;
  const [pw, setPw] = useState({ current_password: '', new_password: '', confirm: '' });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (pw.new_password.length < 8) { toast.error('New password must be at least 8 characters'); return; }
    if (pw.new_password !== pw.confirm) { toast.error('New passwords do not match'); return; }
    setBusy(true);
    try {
      await api().post('/auth/change-password', { current_password: pw.current_password, new_password: pw.new_password });
      toast.success('Password changed');
      setUser({ ...user, must_change_password: false });
      nav('/', { replace: true });
    } catch (err) { toast.error(detailText(err.response?.data?.detail)); } finally { setBusy(false); }
  };
  const set = (k) => (e) => setPw({ ...pw, [k]: e.target.value });
  return (
    <div className="min-h-[70vh] flex items-center justify-center p-4" data-testid="change-password-page">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-orange-100 text-orange-600 mx-auto mb-2"><KeyRound className="w-5 h-5" /></div>
          <h1 className="text-2xl font-bold">{forced ? 'Choose a new password' : 'Change password'}</h1>
          <p className="text-sm text-slate-500" data-testid="change-password-reason">{forced ? 'Your administrator set a temporary password. Please choose your own before continuing.' : `Signed in as ${user?.email}`}</p>
        </CardHeader>
        <CardContent>
          <form onSubmit={submit} className="space-y-3">
            <div><Label>{forced ? 'Temporary password' : 'Current password'}</Label><Input type="password" autoComplete="current-password" value={pw.current_password} onChange={set('current_password')} required className="rounded-sm" data-testid="cp-current-input" /></div>
            <div><Label>New password (8+ characters)</Label><Input type="password" autoComplete="new-password" value={pw.new_password} onChange={set('new_password')} required className="rounded-sm" data-testid="cp-new-input" /></div>
            <div><Label>Confirm new password</Label><Input type="password" autoComplete="new-password" value={pw.confirm} onChange={set('confirm')} required className="rounded-sm" data-testid="cp-confirm-input" /></div>
            <Button type="submit" disabled={busy} className="w-full bg-slate-900 hover:bg-slate-800 rounded-sm" data-testid="cp-submit-btn">{busy ? 'Saving…' : 'Change password'}</Button>
            {forced && <Button type="button" variant="ghost" className="w-full" onClick={logout} data-testid="cp-logout-btn"><LogOut className="w-4 h-4 mr-1" /> Sign out</Button>}
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
